/**
 * The OIDC redirect target — where the hosted login hands the browser back.
 *
 * ⚠️  THIS ROUTE MUST BE EXEMPT FROM THE ROOT LAYOUT'S NAVIGATION GUARD, and it
 *     is (app/_layout.tsx). The guard redirects any unauthenticated route to
 *     /(auth)/login, and at this instant the user is by definition not yet
 *     authenticated — the code that would authenticate them is sitting in the
 *     URL. On 2026-08-02 that combination silently ate a real authorization
 *     code: the IdP redirected correctly, this page loaded, the guard fired,
 *     and the code was gone before anything read it. The login looked like it
 *     had failed when the identity-provider half had worked perfectly.
 *
 * Path is /auth/callback, NOT inside the (auth) group, so it byte-matches the
 * redirect URI registered on the ZITADEL client. The registered value and this
 * file's location have to be changed together.
 */

import { useEffect, useRef, useState } from 'react';
import { View, Text, ActivityIndicator, Pressable } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';

import { completeOidcLogin } from '../../src/auth/completeOidcLogin';
import { suppressAutoLogin } from '../../src/auth/autoLogin';
import { isStepUpReturnRoute } from '../../src/auth/returnRoute';
import { captureException } from '../../src/observability/sentry';
import { useTheme } from '../../src/theme/ThemeContext';
import { useThemedStyles } from '../../src/theme/useThemedStyles';
import type { Theme } from '../../src/theme/types';

export default function OidcCallback() {
  const params = useLocalSearchParams<{
    code?: string;
    state?: string;
    error?: string;
    error_description?: string;
  }>();
  const router = useRouter();
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();

  const [error, setError] = useState<string | null>(null);
  // Expo Router can re-render this screen with the same params; exchanging a
  // single-use code twice fails the second time and would surface as a login
  // error after a login that actually succeeded.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;

    if (params.error) {
      started.current = true;
      // Set with the error, not on the button below, so ANY route back to the
      // sign-in screen from here renders it. On web that screen redirects to the
      // IdP on mount; from a failure it would redirect back into the failure,
      // and the error the user needs to read would flash past on every lap.
      suppressAutoLogin();
      setError(params.error_description || params.error);
      return;
    }
    if (!params.code) return; // params can arrive on a later render

    started.current = true;
    (async () => {
      try {
        const { returnTo } = await completeOidcLogin(
          params.code as string,
          params.state as string | undefined,
        );
        // Replace, so Back does not return to a URL still carrying a spent code.
        //
        // `returnTo` is set only by a step-up (src/auth/stepUp.ts), and is what
        // makes one survivable on web: without it every re-authentication lands
        // on '/' and the root layout re-decides from scratch, which sent a user
        // who asked to reset their account to /restore instead — the screen
        // they were trying to escape. Setup routes are exempt from the guard's
        // redirect (app/_layout.tsx), so landing back on one sticks.
        router.replace(isStepUpReturnRoute(returnTo) ? returnTo : '/');
      } catch (e) {
        captureException(e, { context: 'oidc_callback' });
        suppressAutoLogin();
        setError(e instanceof Error ? e.message : 'Sign-in could not be completed');
      }
    })();
  }, [params.code, params.state, params.error, params.error_description, router]);

  if (error) {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>Sign-in failed</Text>
        <Text style={styles.detail}>{error}</Text>
        <Pressable
          style={styles.button}
          onPress={() => router.replace('/(auth)/login')}
          accessibilityRole="button"
        >
          <Text style={styles.buttonText}>Back to sign in</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ActivityIndicator size="large" color={colors.primary} accessibilityLabel="Completing sign-in" />
      <Text style={styles.detail}>Completing sign-in…</Text>
    </View>
  );
}

const createStyles = (theme: Theme) => ({
  container: {
    flex: 1,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    padding: 24,
    gap: 12,
    backgroundColor: theme.colors.bgSecondary,
  },
  title: {
    fontSize: 20,
    fontWeight: '600' as const,
    color: theme.colors.textPrimary,
  },
  detail: {
    fontSize: 14,
    textAlign: 'center' as const,
    color: theme.colors.textSecondary,
  },
  button: {
    marginTop: 8,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    backgroundColor: theme.colors.primary,
  },
  buttonText: {
    color: '#fff',
    fontWeight: '600' as const,
  },
});
